import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getAdminReleaseNotes, createAdminReleaseNotes } from '../../features/releaseNotes/services/releaseNotesService';

const ReleaseNotesAdminPage = () => {
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newReleaseNote, setNewReleaseNote] = useState({
    version: '',
    date: '',
    sections: [{ title: '', items: [{ description: '' }] }],
  });

  // Fetch release notes when the component loads
  useEffect(() => {
    fetchReleaseNotes();
  }, []);

  const fetchReleaseNotes = async () => {
    try {
      const notes = await getAdminReleaseNotes();
      setReleaseNotes(notes);
    } catch (error) {
      console.error('Error fetching release notes:', error);
    }
  };

  const handleDialogOpen = () => setDialogOpen(true);
  const handleDialogClose = () => setDialogOpen(false);

  const handleInputChange = (field, value) => {
    setNewReleaseNote((prev) => ({ ...prev, [field]: value }));
  };

  const handleSectionChange = (index, field, value) => {
    const updatedSections = [...newReleaseNote.sections];
    updatedSections[index][field] = value;
    setNewReleaseNote((prev) => ({ ...prev, sections: updatedSections }));
  };

  const handleItemChange = (sectionIndex, itemIndex, value) => {
    const updatedSections = [...newReleaseNote.sections];
    updatedSections[sectionIndex].items[itemIndex].description = value;
    setNewReleaseNote((prev) => ({ ...prev, sections: updatedSections }));
  };

  const addSection = () => {
    setNewReleaseNote((prev) => ({
      ...prev,
      sections: [...prev.sections, { title: '', items: [{ description: '' }] }],
    }));
  };

  const addItem = (sectionIndex) => {
    const updatedSections = [...newReleaseNote.sections];
    updatedSections[sectionIndex].items.push({ description: '' });
    setNewReleaseNote((prev) => ({ ...prev, sections: updatedSections }));
  };

  const saveReleaseNote = async () => {
    try {
      await createAdminReleaseNotes(newReleaseNote);
      await fetchReleaseNotes(); // Refetch release notes after adding a new one
      setDialogOpen(false);
      setNewReleaseNote({ version: '', date: '', sections: [{ title: '', items: [{ description: '' }] }] });
    } catch (error) {
      console.error('Error creating release note:', error);
    }
  };

  return (
    <Box sx={{ padding: '20px', maxWidth: '1200px', margin: 'auto' }}>
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        justifyContent="space-between" 
        alignItems={{ xs: 'flex-start', sm: 'center' }} 
        spacing={2} 
        mb={3}
      >
        <Typography variant="h4">Release Notes Admin</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleDialogOpen}>
          Create Release Note
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Version</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Sections</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {releaseNotes.map((note, index) => (
              <TableRow key={index}>
                <TableCell>{note.version}</TableCell>
                <TableCell>{note.date}</TableCell>
                <TableCell>
                  {note.sections.map((section, secIndex) => (
                    <Accordion key={secIndex}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>{section.title}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List>
                          {section.items.map((item, itemIndex) => (
                            <ListItem key={itemIndex}>
                              <ListItemText primary={item.description} />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Release Note</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Version"
            value={newReleaseNote.version}
            onChange={(e) => handleInputChange('version', e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            type="date"
            label="Release Date"
            value={newReleaseNote.date}
            onChange={(e) => handleInputChange('date', e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />

          {newReleaseNote.sections.map((section, sectionIndex) => (
            <Box key={sectionIndex} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label={`Section Title ${sectionIndex + 1}`}
                value={section.title}
                onChange={(e) => handleSectionChange(sectionIndex, 'title', e.target.value)}
                margin="normal"
              />
              {section.items.map((item, itemIndex) => (
                <TextField
                  key={itemIndex}
                  fullWidth
                  label={`Item ${itemIndex + 1}`}
                  value={item.description}
                  onChange={(e) =>
                    handleItemChange(sectionIndex, itemIndex, e.target.value)
                  }
                  margin="normal"
                  sx={{ ml: 2 }}
                />
              ))}
              <Button onClick={() => addItem(sectionIndex)} size="small">
                Add Item
              </Button>
            </Box>
          ))}
          <Button onClick={addSection} size="small">
            Add Section
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button variant="contained" onClick={saveReleaseNote}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReleaseNotesAdminPage;
